import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AssessmentStatus, PatientTrends, ReportType, TrendSeries } from '../../shared/types'
import { api } from '../api'
import { Card, ErrorNote, Pill, Spinner } from '../components'
import { dateLabel } from '../format'
import { useSession } from '../session-context'
import { changeText, TrendCard } from '../../report/TrendChart'

const STATUS_LABEL: Record<AssessmentStatus, string> = { draft: 'Draft', in_review: 'In review', signed: 'Signed', delivered: 'In Semble' }
const STATUS_TONE: Record<AssessmentStatus, 'ok' | 'warn' | 'bad' | 'neutral'> = { draft: 'neutral', in_review: 'warn', signed: 'ok', delivered: 'ok' }
const TYPE_LABEL: Record<ReportType, string> = { full: 'Prevent51 report', summary: 'Short results report' }

function ageFrom(dob: string | null): string {
  if (dob === null) return ''
  const d = new Date(`${dob}T00:00:00Z`)
  const now = new Date()
  let a = now.getUTCFullYear() - d.getUTCFullYear()
  if (now.getUTCMonth() < d.getUTCMonth() || (now.getUTCMonth() === d.getUTCMonth() && now.getUTCDate() < d.getUTCDate())) a -= 1
  return `${a}`
}

/** One patient: who she is, her screens, where her key markers are now and how they have moved. */
export function PatientScreen({ id, onBack, onOpenAssessment }: { id: number; onBack: () => void; onOpenAssessment: (id: number) => void }) {
  const { can } = useSession()
  const [t, setT] = useState<PatientTrends | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => setT(await api.patientTrends(id)), [id])
  useEffect(() => { load().catch(setError) }, [load])

  const key = useMemo(() => (t?.series ?? []).filter((s) => s.isKey).sort((a, b) => a.keyOrder - b.keyOrder), [t])
  const groups = useMemo(() => {
    if (t === null) return []
    const parents: string[] = []
    for (const s of t.sections) { const p = s.parentTitle ?? s.title; if (!parents.includes(p)) parents.push(p) }
    return parents.map((parent) => {
      const codes = t.sections.filter((s) => (s.parentTitle ?? s.title) === parent).map((s) => s.code)
      return { parent, series: t.series.filter((s) => codes.includes(s.sectionCode) && !s.isKey) }
    }).filter((g) => g.series.length > 0)
  }, [t])

  if (t === null) return error === null ? <Spinner /> : <ErrorNote error={error} />
  const p = t.patient
  const screens = t.assessments.length

  async function start(reportType: ReportType) {
    setBusy(true)
    try { const a = await api.createAssessment(id, new Date().toISOString().slice(0, 10), reportType); onOpenAssessment(a.id) }
    catch (err) { setError(err) } finally { setBusy(false) }
  }

  return (
    <>
      <div className="crumbs"><button type="button" className="linkish" onClick={onBack}>← Patients</button></div>
      <ErrorNote error={error} />
      <Card title={`${p.lastName}, ${p.firstName}`}
        subtitle={`DOB ${dateLabel(p.dob)}${p.dob !== null ? ` · age ${ageFrom(p.dob)}` : ''}${p.email !== null ? ` · ${p.email}` : ''}${p.sembleId !== null ? ` · Semble ${p.sembleId}` : ''}`}
        actions={can('nurse') && (
          <>
            <button type="button" disabled={busy} onClick={() => void start('full')}>New Prevent51 assessment</button>{' '}
            <button type="button" disabled={busy} onClick={() => void start('summary')}>New short results report</button>
          </>
        )}>
        <table>
          <thead><tr><th>Date</th><th>Report</th><th>Status</th><th>Results</th><th></th></tr></thead>
          <tbody>
            {t.assessments.length === 0 && <tr><td colSpan={5} className="muted">No assessments yet.</td></tr>}
            {t.assessments.map((a) => (
              <tr key={a.id}>
                <td><button type="button" className="linkish" onClick={() => onOpenAssessment(a.id)}>{dateLabel(a.assessmentDate)}</button></td>
                <td>{TYPE_LABEL[a.reportType]}</td>
                <td><Pill tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Pill>{a.latestVersion !== null && <span className="muted small"> v{a.latestVersion}</span>}</td>
                <td className="muted">{a.resultCount}</td>
                <td><button type="button" className="quiet" onClick={() => onOpenAssessment(a.id)}>open</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card title="Key markers" subtitle={screens > 1 ? `Latest value, its Clinic51 band, and the change across ${screens} screens` : 'Latest value and its Clinic51 band. Trends appear from the second screen.'}
        actions={t.series.some((s) => s.points.length > 1) && <a className="link-button" href={api.trendsPdfUrl(id)} target="_blank" rel="noreferrer">Open trends PDF</a>}>
        {key.length === 0 ? <p className="note">No key markers have results yet.</p> : (
          <div className="trend-grid">{key.map((s) => <TrendCard key={s.code} series={s} />)}</div>
        )}
        <p className="note">Key markers are chosen on the Reference ranges tab.</p>
      </Card>

      {groups.map((g) => (
        <details key={g.parent} className="card trend-group">
          <summary><span>{g.parent}</span><span className="card-subtitle">{g.series.length} markers</span></summary>
          <div className="body">
            <table className="trend-table">
              <thead><tr><th>Marker</th>{t.assessments.slice().reverse().map((a) => <th key={a.id}>{dateLabel(a.assessmentDate)}</th>)}<th>Change</th></tr></thead>
              <tbody>{g.series.map((s) => <SeriesRow key={s.code} s={s} t={t} />)}</tbody>
            </table>
            <div className="trend-grid">{g.series.filter((s) => s.points.length > 1).map((s) => <TrendCard key={s.code} series={s} />)}</div>
          </div>
        </details>
      ))}
    </>
  )
}

function SeriesRow({ s, t }: { s: TrendSeries; t: PatientTrends }) {
  const last = s.points[s.points.length - 1]
  const prev = s.points.length > 1 ? s.points[s.points.length - 2] : null
  return (
    <tr>
      <td>{s.name} <span className="muted small">{s.unit}</span></td>
      {t.assessments.slice().reverse().map((a) => {
        const pt = s.points.find((x) => x.assessmentId === a.id)
        return <td key={a.id}>{pt === undefined ? <span className="muted">—</span> : <span className={`chip ${pt.bandColour ?? 'grey'}`} title={pt.bandLabel ?? 'No band'}>{pt.valueText}</span>}</td>
      })}
      <td className="muted small">{last !== undefined ? changeText(last.value, prev, s.decimals) ?? '' : ''}</td>
    </tr>
  )
}
